import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { UserPlus, Mail, Lock, User, Building, Loader2, ArrowRight, Sun, Moon } from 'lucide-react';
import api from '../api/axios';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();                          // ← ADD
  const pendingPlan = searchParams.get('plan');  
  const [formData, setFormData] = useState({ 
    full_name: '', 
    email: '', 
    password: '', 
    organization_name: '' 
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Backend returns "Email already registered" when the account exists —
  // offer a direct route to login instead of a dead-end error.
  const isEmailTaken = /already\s+(registered|exists|in use)/i.test(error);

  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') !== 'light');
  useEffect(() => {
    if (isDark) { document.documentElement.classList.add('dark'); localStorage.setItem('theme', 'dark'); } 
    else { document.documentElement.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
  }, [isDark]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await api.post('/register', formData);
      // Redirect to login, carrying the plan forward
      const loginPath = pendingPlan ? `/login?plan=${pendingPlan}` : '/login';  // ← CHANGE
      setTimeout(() => navigate(loginPath), 500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans flex items-center justify-center relative overflow-hidden overflow-x-hidden transition-colors duration-500 px-4 py-4 sm:py-6">
      <style>{customStyles}</style>

      {/* Theme Toggle Button */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50">
        <button 
          onClick={() => setIsDark(!isDark)} 
          className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none ${isDark ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-200 border border-slate-300 shadow-sm'}`}
          title="Toggle Theme"
        >
          <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full transition-transform duration-300 ${isDark ? 'translate-x-7 bg-indigo-400' : 'translate-x-1 bg-white shadow-sm'}`}>
            {isDark ? <Moon size={10} className="text-[#0a0a0a]" /> : <Sun size={10} className="text-slate-400" />}
          </span>
        </button>
      </div>

      {/* Ambient Glows */}
      <div className="absolute top-[10%] left-[20%] w-[40%] h-[40%] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none"></div>
      <div className="absolute bottom-[10%] right-[20%] w-[40%] h-[40%] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none" style={{ animationDelay: '2s' }}></div>

      <div className="w-full max-w-md relative z-10 animate-in fade-in zoom-in-95 duration-500">
        
        {/* Brand Header - Tighter margins */}
        <div className="flex flex-col items-center justify-center mb-5 sm:mb-6 mt-2">
          <div className="w-12 h-12 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-[1rem] flex items-center justify-center mb-3 shadow-md dark:shadow-2xl backdrop-blur-md">
            <UserPlus size={24} className="text-indigo-600 dark:text-indigo-400" strokeWidth={1.5} />
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-wide text-slate-900 dark:text-white text-center transition-colors">Create Account</h1>
        </div>

        {/* Auth Card - Tighter padding and input gaps */}
        <form onSubmit={handleSubmit} className="bg-white/80 dark:bg-[#0a0a0a]/50 backdrop-blur-xl border border-slate-200 dark:border-white/10 p-5 sm:p-8 rounded-[2rem] shadow-xl dark:shadow-2xl flex flex-col gap-5">
          
          <div className="space-y-3.5">
            {/* FULL NAME FIELD */}
            <div>
              <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-1.5">
                <User size={12} className="text-indigo-500 dark:text-indigo-400"/> Full Name
              </label>
              <input 
                type="text" required disabled={isLoading}
                placeholder="e.g. John Doe" 
                value={formData.full_name} 
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-3.5 py-2.5 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
              />
            </div>

            {/* ORGANIZATION FIELD */}
            <div>
              <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-1.5">
                <Building size={12} className="text-violet-500 dark:text-violet-400"/> Organization Name
              </label>
              <input 
                type="text" required disabled={isLoading}
                placeholder="e.g. Acme Corp" 
                value={formData.organization_name} 
                onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-3.5 py-2.5 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
              />
            </div>

            {/* EMAIL FIELD */}
            <div>
              <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-1.5">
                <Mail size={12} className="text-indigo-500 dark:text-indigo-400"/> Email Address
              </label>
              <input 
                type="email" required disabled={isLoading}
                placeholder="you@company.com" 
                value={formData.email} 
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-3.5 py-2.5 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
              />
            </div>

            {/* PASSWORD FIELD */}
            <div>
              <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-1.5">
                <Lock size={12} className="text-indigo-500 dark:text-indigo-400"/> Password
              </label>
              <input 
                type="password" required disabled={isLoading}
                placeholder="••••••••" 
                value={formData.password} 
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-3.5 py-2.5 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
              />
            </div>

            {error && (
              <div className="text-rose-600 dark:text-rose-500 text-xs font-medium bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 p-2.5 rounded-xl">
                {error}
                {isEmailTaken && (
                  <>
                    {' - '}
                    <Link
                      to={pendingPlan ? `/login?plan=${pendingPlan}` : '/login'}
                      className="font-bold underline underline-offset-2 hover:text-rose-700 dark:hover:text-rose-400 transition-colors"
                    >
                      Sign in instead
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>

          <button 
            type="submit" disabled={isLoading || !formData.email || !formData.password || !formData.full_name || !formData.organization_name}
            className="w-full bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white tracking-[0.1em] [word-spacing:-0.05em] rounded-xl py-3.5 text-xs font-bold tracking-wide transition-all shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 mt-1"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin"/> : 'Create Account'}
            {!isLoading && <ArrowRight size={16} strokeWidth={2}/>}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-slate-500 dark:text-slate-400 text-[12px] font-medium tracking-[0.1em] [word-spacing:-0.05em]">
            Already have an account?{' '}
            <Link to="/login" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-white transition-colors ml-1">
              Sign in
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
};

export default Register;